import { useQuery } from "react-query";
import { Activity, CalendarClock, ExternalLink, FolderGit2, GitBranch, Globe, ListChecks, ScrollText, ShieldAlert, Users, Zap } from "lucide-react";
import { Button } from "../../../ui/button";
import { saasApi, saasErrorMessage } from "../shared/saasApi";
import { formatDate, formatNumber, formatRelative, humanize } from "../shared/saasFormat";
import { ActivityTimelineChart } from "../shared/SaasCharts";
import { BreakdownBars } from "../shared/SaasUi";
import DetailModal, { DetailChip, DetailEmpty, DetailField, DetailOverview, DetailPanel, DetailSection } from "./DetailModal";
import EventList from "./EventList";
import FindingCard from "./FindingCard";
import GrantsTable from "./GrantsTable";
import { WorkspaceContext } from "./workspaceContext";

export default function ResourceDetailModal({ context, connectionId, resourceId, onClose }: {
    context: WorkspaceContext;
    connectionId: number;
    resourceId: string;
    onClose: () => void;
}) {
    const query = useQuery(["saas-resource-detail", context.provider, context.tenantName, connectionId, resourceId, context.range], () =>
        saasApi.resourceDetail(context.provider, context.tenantName, connectionId, resourceId, context.range));
    const detail = query.data;
    const resource = detail?.resource;

    return (
        <DetailModal
            onClose={onClose}
            title={resource?.fullName ?? "Loading…"}
            subtitle={resource ? resource.description || humanize(resource.resourceType) : undefined}
            icon={<FolderGit2 />}
            chips={resource && (
                <>
                    <DetailChip tone="type">{humanize(resource.resourceType)}</DetailChip>
                    {resource.visibility && <DetailChip tone={resource.visibility === "public" ? "severity" : undefined}>{humanize(resource.visibility)}</DetailChip>}
                    {resource.archived && <DetailChip>Archived</DetailChip>}
                    {resource.fork && <DetailChip>Fork</DetailChip>}
                    {resource.defaultBranchProtected === false && <DetailChip tone="severity">Default branch unprotected</DetailChip>}
                </>
            )}
            actions={resource?.webUrl && (
                <Button variant="outline" className="saas-small-button" asChild>
                    <a href={resource.webUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-4 h-4" />
                        Open
                    </a>
                </Button>
            )}
        >
            {query.isLoading ? (
                <div className="space-y-4">
                    <div className="h-[66px] rounded-xl border border-border animate-pulse" />
                    <div className="h-64 rounded-xl border border-border animate-pulse" />
                </div>
            ) : query.isError || !detail || !resource ? (
                <DetailEmpty>{saasErrorMessage(query.error, "Unable to load this resource")}</DetailEmpty>
            ) : (
                <>
                    <DetailOverview>
                        <DetailField icon={<Activity />} label="Events" value={`${formatNumber(detail.activity.eventCount)} · ${formatNumber(detail.activity.highRiskCount)} high risk`} />
                        <DetailField icon={<Users />} label="Members" value={`${formatNumber(resource.memberCount)} · ${formatNumber(resource.privilegedMemberCount)} privileged`} />
                        <DetailField icon={<CalendarClock />} label="Last activity" value={formatRelative(resource.lastActivityAt, "No activity recorded")} />
                        <DetailField icon={<ShieldAlert />} label="Open findings" value={formatNumber(detail.findings.length)} />
                        <DetailField icon={<GitBranch />} label="Default branch"
                                     value={resource.defaultBranch
                                         ? `${resource.defaultBranch}${resource.defaultBranchProtected === false ? " · unprotected" : resource.defaultBranchProtected ? " · protected" : ""}`
                                         : "—"} />
                        <DetailField icon={<Globe />} label="Visibility" value={humanize(resource.visibility) || "—"} />
                        <DetailField icon={<FolderGit2 />} label="Parent" value={resource.parentFullName ?? "—"} title={resource.parentFullName ?? undefined} />
                        <DetailField icon={<CalendarClock />} label="Created" value={formatDate(resource.platformCreatedAt)} />
                    </DetailOverview>

                    {detail.findings.length > 0 && (
                        <DetailSection icon={<ListChecks />} title="Open findings">
                            <div className="space-y-2">
                                {detail.findings.map((finding) => (
                                    <FindingCard key={finding.id} finding={finding} context={context} compact onChanged={() => query.refetch()} />
                                ))}
                            </div>
                        </DetailSection>
                    )}

                    <DetailSection icon={<Activity />} title="Activity">
                        <DetailPanel>
                            <ActivityTimelineChart activity={detail.activity} height={220} />
                        </DetailPanel>
                    </DetailSection>

                    <DetailSection icon={<Users />} title="Members and access">
                        <GrantsTable grants={detail.grants} mode="resource" onIdentity={(login) => context.openIdentity(connectionId, login)} />
                    </DetailSection>

                    <div className="saas-split">
                        <DetailSection icon={<Users />} title="Most active identities">
                            <DetailPanel className="max-h-80 overflow-y-auto">
                                <BreakdownBars items={detail.activity.actors} onSelect={(item) => context.openIdentity(connectionId, item.key)} />
                            </DetailPanel>
                        </DetailSection>
                        <DetailSection icon={<Zap />} title="Top actions">
                            <DetailPanel className="max-h-80 overflow-y-auto">
                                <BreakdownBars items={detail.activity.actions} onSelect={(item) => context.openEvents({ resource: resource.fullName, action: item.key })} />
                            </DetailPanel>
                        </DetailSection>
                    </div>

                    <DetailSection
                        icon={<ScrollText />}
                        title="Recent events"
                        action={<button type="button" className="saas-section-link" onClick={() => context.openEvents({ resource: resource.fullName })}>Open in audit log</button>}
                    >
                        <EventList events={detail.recentEvents.slice(0, 25)} onOpen={context.openEvent} />
                    </DetailSection>
                </>
            )}
        </DetailModal>
    );
}
